import type { Me } from "@clubcore/api";
import { matchPath } from "react-router";
import { modules as allModules } from "../modules";
import type { ModuleDefinition } from "../modules/types";
import type { ClubProfile } from "./club-selection";

export const HOME = "/";
/** Signed-in pages outside the modules (routes.tsx) that check what the person may do themselves, such as approving a POS exit
 * from a scanned QR code: a sign-in on the way there must land on them. */
export const SHELL_PAGES = ["/pos-exit-approval", "/choose-club", "/no-club", "/settings"];

/** Whether `can` is allowed to open `path`: it must be a page a module declares, and the user must hold what that page needs. */
export function pathAllowed(path: string, can: (permission: string | undefined) => boolean, modules: readonly ModuleDefinition[] = allModules): boolean {
  const pathname = path.split(/[?#]/)[0] || HOME;
  if (pathname === HOME || SHELL_PAGES.includes(pathname)) return true;
  for (const module of modules) {
    for (const route of module.routes) {
      if (!matchPath({ path: `/${route.path}`, end: true }, pathname)) continue;
      return can(route.permission) && (!route.anyPermission || route.anyPermission.some(can));
    }
  }
  return false;
}

/** The screen a built-in role works from all day. A combined or custom role has no entry: such a person's Home shows their mix. */
export const WORK_SCREENS: Readonly<Record<string, string>> = {
  Server: "/pos/checks",
  "Front desk": "/frontdesk",
  "Pro shop": "/teesheet",
  Housekeeping: "/lodging/housekeeping",
};

/** Marks a sign-in's arrival on a work screen. */
export const ARRIVING = "arriving=1";

/** The work screen of this role, when the person may open it. */
export function workScreenFor(role: string, can: (permission: string | undefined) => boolean, modules: readonly ModuleDefinition[] = allModules): string | undefined {
  const path = WORK_SCREENS[role];
  return path && pathAllowed(path, can, modules) ? path : undefined;
}

/**
 * Where a person lands after signing in: the page they asked for if their permissions allow it, otherwise their role's work
 * screen, otherwise Home (which shows what they can reach). The club is the one they will work in, when that is already clear;
 * otherwise Home, and the club choice follows.
 */
export function landingFor(me: Me, clubSlug: string | null, requested: string | undefined, modules: readonly ModuleDefinition[] = allModules): string {
  const asked = requested && requested !== HOME && requested.startsWith("/") && !requested.startsWith("//") ? requested : undefined;
  if (me.user.is_platform_staff) return asked && pathAllowed(asked, () => true, modules) ? asked : HOME;
  const memberships = me.clubs as ClubProfile[];
  const club = memberships.find((c) => c.slug === clubSlug) ?? (memberships.length === 1 ? memberships[0] : undefined);
  if (!club) return HOME;
  const granted = new Set(club.permissions);
  const can = (permission: string | undefined) => !permission || granted.has(permission);
  if (asked && pathAllowed(asked, can, modules)) return asked;
  const work = workScreenFor(club.role, can, modules);
  // The desk screen sends someone with no desk on to other work, but only on arrival: from the menu it explains why it is empty.
  return work === "/frontdesk" ? `${work}?${ARRIVING}` : work ?? HOME;
}

/**
 * The page a sign-in screen was asked to return to is kept in the router's history state. It belongs to the person who was
 * signed in then; another person signing in on the same browser must not inherit it.
 */
export interface ReturnState {
  from?: string;
  expired?: boolean;
  /** Who was signed in when the request was made; absent when nobody was. */
  userId?: string;
}

export function returnPathFor(state: ReturnState | null | undefined, userId: string): string | undefined {
  if (!state?.from) return undefined;
  return state.userId && state.userId !== userId ? undefined : state.from;
}
